import React, { useState } from 'react';
import {  Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaGraduationCap, FaUser, FaFileAlt, FaCheckCircle, FaUniversity, FaCalendarAlt, FaPhone, FaEnvelope, FaMapMarkerAlt } from 'react-icons/fa';
import { GiDiploma, GiReceiveMoney } from 'react-icons/gi';
import { IoMdSchool } from 'react-icons/io';
import { db } from '../FirebaseComp/firebase'; // Make sure you have firebase configured
import { collection, addDoc } from 'firebase/firestore';
import './apply.css';

const Apply = () => {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    personalInfo: {
      fullName: '',
      dob: '',
      email: '',
      phone: '',
      address: '',
      nationality: ''
    },
    educationBackground: {
      educationLevel: '',
      institution: '',
      completionYear: '',
      major: '',
      gpa: ''
    },
    programSelection: {
      programId: '',
      programName: ''
    },
    applicationDate: new Date().toISOString(),
    status: 'submitted'
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const programs = [
    {
      id: 1,
      name: "Bachelor of Computer Science",
      duration: "4 Years",
      intake: "January, May, September",
      requirements: "High School Diploma with Math & Physics"
    },
    {
      id: 2,
      name: "Bachelor of Electrical Engineering",
      duration: "4 Years",
      intake: "January, September",
      requirements: "High School Diploma with Math & Physics"
    },
    {
      id: 3,
      name: "Bachelor of Business Administration",
      duration: "3 Years",
      intake: "January, May, September",
      requirements: "High School Diploma"
    },
    {
      id: 4,
      name: "Master of Data Science",
      duration: "2 Years",
      intake: "January, September",
      requirements: "Bachelor's Degree in Related Field"
    },
    {
      id: 5,
      name: "PhD in Engineering",
      duration: "3-5 Years",
      intake: "Rolling Admission",
      requirements: "Master's Degree in Engineering"
    }
  ];

  const nextStep = () => {
    // Validate current step before proceeding
    if (validateStep(step)) {
      setStep(step + 1);
    }
  };

  const prevStep = () => setStep(step - 1);

  const validateStep = (currentStep) => {
    let isValid = true;
    const newErrors = {};

    if (currentStep === 1) {
      if (!formData.personalInfo.fullName) {
        newErrors.fullName = 'Full name is required';
        isValid = false;
      }
      if (!formData.personalInfo.dob) {
        newErrors.dob = 'Date of birth is required';
        isValid = false;
      }
      if (!formData.personalInfo.email) {
        newErrors.email = 'Email is required';
        isValid = false;
      } else if (!/^\S+@\S+\.\S+$/.test(formData.personalInfo.email)) {
        newErrors.email = 'Email is invalid';
        isValid = false;
      }
      if (!formData.personalInfo.phone) {
        newErrors.phone = 'Phone number is required';
        isValid = false;
      }
      if (!formData.personalInfo.address) {
        newErrors.address = 'Address is required';
        isValid = false;
      }
      if (!formData.personalInfo.nationality) {
        newErrors.nationality = 'Nationality is required';
        isValid = false;
      }
    } else if (currentStep === 2) {
      if (!formData.educationBackground.educationLevel) {
        newErrors.educationLevel = 'Education level is required';
        isValid = false;
      }
      if (!formData.educationBackground.institution) {
        newErrors.institution = 'Institution name is required';
        isValid = false;
      }
      if (!formData.educationBackground.completionYear) {
        newErrors.completionYear = 'Completion year is required';
        isValid = false;
      }
      if (!formData.educationBackground.major) {
        newErrors.major = 'Major/Field of study is required';
        isValid = false;
      }
    } else if (currentStep === 3) {
      if (!formData.programSelection.programId) {
        newErrors.program = 'Please select a program';
        isValid = false;
      }
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleChange = (e, section) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [section]: {
        ...formData[section],
        [name]: value
      }
    });
  };

  const submitApplication = async (e) => {
    e.preventDefault();
    
    if (!validateStep(step)) return;
    
    setIsSubmitting(true);
    
    try {
      // Save to Firestore
      const docRef = await addDoc(collection(db, 'applicants'), formData);
      console.log("Document written with ID: ", docRef.id);
      nextStep(); // Move to confirmation
    } catch (error) {
      console.error("Error adding document: ", error);
      setErrors({ submit: 'Failed to submit application. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <motion.div 
      className="apply-container"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      {/* Application Header */}
      <div className="apply-header">
        <h1><FaGraduationCap /> Apply to Asian Polytechnic</h1>
        <p>Begin your journey to academic excellence with our streamlined application process</p>
      </div>

      {/* Application Progress */}
      <div className="progress-steps">
        <div className={`step ${step >= 1 ? 'active' : ''}`}>
          <div className="step-number">1</div>
          <div className="step-label">Personal Info</div>
        </div>
        <div className={`step ${step >= 2 ? 'active' : ''}`}>
          <div className="step-number">2</div>
          <div className="step-label">Education</div>
        </div>
        <div className={`step ${step >= 3 ? 'active' : ''}`}>
          <div className="step-number">3</div>
          <div className="step-label">Program</div>
        </div>
        <div className={`step ${step >= 4 ? 'active' : ''}`}>
          <div className="step-number">4</div>
          <div className="step-label">Submit</div>
        </div>
      </div>

      {/* Application Form */}
      <div className="application-form">
        {step === 1 && (
          <motion.div
            initial={{ x: -50, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ duration: 0.5 }}
            className="form-section"
          >
            <h2><FaUser /> Personal Information</h2>
            <div className="form-grid">
              <div className="form-group">
                <label>Full Name</label>
                <input 
                  type="text" 
                  name="fullName" 
                  onChange={(e) => handleChange(e, 'personalInfo')} 
                  required 
                />
                {errors.fullName && <span className="error-message">{errors.fullName}</span>}
              </div>
              <div className="form-group">
                <label>Date of Birth</label>
                <input 
                  type="date" 
                  name="dob" 
                  onChange={(e) => handleChange(e, 'personalInfo')} 
                  required 
                />
                {errors.dob && <span className="error-message">{errors.dob}</span>}
              </div>
              <div className="form-group">
                <label>Email</label>
                <input 
                  type="email" 
                  name="email" 
                  onChange={(e) => handleChange(e, 'personalInfo')} 
                  required 
                />
                {errors.email && <span className="error-message">{errors.email}</span>}
              </div>
              <div className="form-group">
                <label>Phone Number</label>
                <input 
                  type="tel" 
                  name="phone" 
                  onChange={(e) => handleChange(e, 'personalInfo')} 
                  required 
                />
                {errors.phone && <span className="error-message">{errors.phone}</span>}
              </div>
              <div className="form-group">
                <label>Address</label>
                <textarea 
                  name="address" 
                  onChange={(e) => handleChange(e, 'personalInfo')} 
                  required 
                />
                {errors.address && <span className="error-message">{errors.address}</span>}
              </div>
              <div className="form-group">
                <label>Nationality</label>
                <input 
                  type="text" 
                  name="nationality" 
                  onChange={(e) => handleChange(e, 'personalInfo')} 
                  required 
                />
                {errors.nationality && <span className="error-message">{errors.nationality}</span>}
              </div>
            </div>
            <div className="form-actions">
              <button type="button" className="next-btn" onClick={nextStep}>
                Next: Education Background
              </button>
            </div>
          </motion.div>
        )}

        {step === 2 && (
          <motion.div
            initial={{ x: -50, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ duration: 0.5 }}
            className="form-section"
          >
            <h2><IoMdSchool /> Education Background</h2>
            <div className="form-grid">
              <div className="form-group">
                <label>Highest Education Level</label>
                <select 
                  name="educationLevel" 
                  onChange={(e) => handleChange(e, 'educationBackground')} 
                  required
                >
                  <option value="">Select</option>
                  <option value="high_school">High School</option>
                  <option value="diploma">Diploma</option>
                  <option value="bachelor">Bachelor's Degree</option>
                  <option value="master">Master's Degree</option>
                  <option value="phd">PhD</option>
                </select>
                {errors.educationLevel && <span className="error-message">{errors.educationLevel}</span>}
              </div>
              <div className="form-group">
                <label>Institution Name</label>
                <input 
                  type="text" 
                  name="institution" 
                  onChange={(e) => handleChange(e, 'educationBackground')} 
                  required 
                />
                {errors.institution && <span className="error-message">{errors.institution}</span>}
              </div>
              <div className="form-group">
                <label>Year of Completion</label>
                <input 
                  type="number" 
                  name="completionYear" 
                  onChange={(e) => handleChange(e, 'educationBackground')} 
                  required 
                />
                {errors.completionYear && <span className="error-message">{errors.completionYear}</span>}
              </div>
              <div className="form-group">
                <label>Major/Field of Study</label>
                <input 
                  type="text" 
                  name="major" 
                  onChange={(e) => handleChange(e, 'educationBackground')} 
                  required 
                />
                {errors.major && <span className="error-message">{errors.major}</span>}
              </div>
              <div className="form-group">
                <label>GPA/Score (if applicable)</label>
                <input 
                  type="text" 
                  name="gpa" 
                  onChange={(e) => handleChange(e, 'educationBackground')} 
                />
              </div>
            </div>
            <div className="form-actions">
              <button type="button" className="prev-btn" onClick={prevStep}>
                Back
              </button>
              <button type="button" className="next-btn" onClick={nextStep}>
                Next: Program Selection
              </button>
            </div>
          </motion.div>
        )}

        {step === 3 && (
          <motion.div
            initial={{ x: -50, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ duration: 0.5 }}
            className="form-section"
          >
            <h2><FaUniversity /> Program Selection</h2>
            <div className="programs-list">
              {programs.map(program => (
                <div 
                  key={program.id} 
                  className={`program-card ${formData.programSelection.programId === program.id ? 'selected' : ''}`}
                  onClick={() => setFormData({
                    ...formData,
                    programSelection: {
                      ...formData.programSelection,
                      programId: program.id,
                      programName: program.name
                    }
                  })}
                >
                  <h3>{program.name}</h3>
                  <div className="program-details">
                    <p><strong>Duration:</strong> {program.duration}</p>
                    <p><strong>Intake:</strong> {program.intake}</p>
                    <p><strong>Requirements:</strong> {program.requirements}</p>
                  </div>
                  <div className="select-indicator">
                    {formData.programSelection.programId === program.id && <FaCheckCircle />}
                  </div>
                </div>
              ))}
            </div>
            <div className="form-actions">
              <button type="button" className="prev-btn" onClick={prevStep}>
                Back
              </button>
              <button 
                type="button" 
                className="next-btn" 
                onClick={nextStep}
                disabled={!formData.programSelection.programId}
              >
                Review and Submit
              </button>
            </div>
          </motion.div>
        )}

        {step === 4 && (
          <motion.div
            initial={{ x: -50, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ duration: 0.5 }}
            className="form-section"
          >
            <h2><FaCheckCircle /> Review Your Application</h2>
            <div className="review-section">
              <h3>Personal Information</h3>
              <div className="review-grid">
                <div>
                  <p><strong>Full Name:</strong> {formData.personalInfo.fullName || 'Not provided'}</p>
                  <p><strong>Email:</strong> {formData.personalInfo.email || 'Not provided'}</p>
                </div>
                <div>
                  <p><strong>Date of Birth:</strong> {formData.personalInfo.dob || 'Not provided'}</p>
                  <p><strong>Phone:</strong> {formData.personalInfo.phone || 'Not provided'}</p>
                </div>
              </div>
            </div>

            <div className="review-section">
              <h3>Education Background</h3>
              <div className="review-grid">
                <div>
                  <p><strong>Education Level:</strong> {formData.educationBackground.educationLevel || 'Not provided'}</p>
                  <p><strong>Institution:</strong> {formData.educationBackground.institution || 'Not provided'}</p>
                </div>
                <div>
                  <p><strong>Major:</strong> {formData.educationBackground.major || 'Not provided'}</p>
                  <p><strong>Completion Year:</strong> {formData.educationBackground.completionYear || 'Not provided'}</p>
                </div>
              </div>
            </div>

            <div className="review-section">
              <h3>Program Selection</h3>
              <p><strong>Selected Program:</strong> {formData.programSelection.programName || 'Not selected'}</p>
            </div>

            <div className="document-instructions">
              <h3><FaFileAlt /> Document Submission Instructions</h3>
              <p>Please mail the following documents to our admissions office:</p>
              <ul>
                <li>Academic Transcripts (official copies)</li>
                <li>Identification Document (passport or national ID copy)</li>
                <li>English Proficiency Test Results (if applicable)</li>
                <li>Statement of Purpose</li>
              </ul>
              <p><strong>Mailing Address:</strong><br />
              Admissions Office<br />
              Asian Polytechnic<br />
              123 University Avenue<br />
              Education City, 10001</p>
            </div>

            <div className="form-actions">
              <button type="button" className="prev-btn" onClick={prevStep}>
                Back
              </button>
              <button 
                type="button" 
                className="submit-btn" 
                onClick={submitApplication}
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Submitting...' : 'Submit Application'}
              </button>
              {errors.submit && <div className="submit-error">{errors.submit}</div>}
            </div>
          </motion.div>
        )}

        {step === 5 && (
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.5 }}
            className="confirmation-section"
          >
            <div className="confirmation-content">
              <FaCheckCircle size={80} className="success-icon" />
              <h2>Application Submitted Successfully!</h2>
              <p>Thank you for applying to Asian Polytechnic. Your application has been received and is being processed.</p>
              <div className="confirmation-details">
                <p><strong>Application ID:</strong> AP-{Math.floor(Math.random() * 1000000)}</p>
                <p><strong>Program:</strong> {formData.programSelection.programName}</p>
                <p>We will contact you at <strong>{formData.personalInfo.email}</strong> within 5-7 business days.</p>
              </div>
              <div className="document-reminder">
                <h3>Important Next Step:</h3>
                <p>Please remember to mail your supporting documents to our admissions office at:</p>
                <p className="mailing-address">
                  Admissions Office<br />
                  Asian Polytechnic<br />
                  123 University Avenue<br />
                  Education City, 10001
                </p>
                <p>Your application will be processed once we receive all required documents.</p>
              </div>
              <div className="next-steps">
                <h3>Next Steps:</h3>
                <ol>
                  <li>Check your email for application confirmation</li>
                  <li>Mail your supporting documents within 7 days</li>
                  <li>Complete any additional requirements if requested</li>
                  <li>Prepare for potential interviews (for some programs)</li>
                  <li>Receive admission decision within 2-3 weeks</li>
                </ol>
              </div>
              <button className="home-btn">Return to Homepage</button>
            </div>
          </motion.div>
        )}
      </div>

      {/* Application Support Sidebar */}
      <div className="support-sidebar">
        <div className="support-card">
          <h3><FaPhone /> Contact Admissions</h3>
          <p>Have questions about the application process?</p>
          <p><strong>Phone:</strong> +1 (234) 567-8900</p>
          <p><strong>Email:</strong> admissions@asianpoly.edu</p>
          <p><strong>Hours:</strong> Mon-Fri, 9AM-5PM</p>
        </div>

        <div className="support-card">
          <h3><GiReceiveMoney /> Financial Aid</h3>
          <p>Learn about scholarships, grants, and payment plans available to qualified students.</p>
          <Link to='/contactus'>
          <button className="support-btn">Explore Financial Aid</button></Link>
        </div>

        <div className="support-card">
          <h3><GiDiploma /> Admission Requirements</h3>
          <p>Review the specific requirements for your program of interest.</p>
            <Link to='/contactus'>
          <button className="support-btn">View Requirements</button></Link>
        </div>

        <div className="support-card">
          <h3><FaCalendarAlt /> Important Dates</h3>
          <ul className="dates-list">
            <li><strong>Fall Intake Deadline:</strong> July 15</li>
            <li><strong>Spring Intake Deadline:</strong> November 15</li>
            <li><strong>Scholarship Deadline:</strong> March 1</li>
          </ul>
        </div>
      </div>
    </motion.div>
  );
};

export default Apply;